import { BCInputSearchRef } from '../BCInputSearch';
import * as React from 'react';
import BCCodeEditor, { ICodeEditor, IMonacoEditor } from '../BCCodeEditor';
import { IFieldType } from '../BCTreeSelect/entities';
import BCTreeview, { ITreeviewItem, ITreeviewSelectEventArgs } from '../BCTreeview';
import Styles from './assets/style.scss';
import { IFormulaEditorProps } from './entities';
import {
	getConstants,
	getMenuItems,
	getOperators,
	getStatistics,
	getTrigonometry
} from './helpers/items';
import FormulaEditorModal from './helpers/modal/FormulaEditorModal';

const mainMenuScrollbarStyles: React.CSSProperties = { height: 290 };
const scrollbarStyles: React.CSSProperties = { height: 250 };

const BCFormulaEditor = (props: IFormulaEditorProps) => {
	const [selectedKeys, setSelectedKeys] = React.useState<string[]>([]);
	const monaco = React.useRef<IMonacoEditor>(null);
	const editor = React.useRef<ICodeEditor>(null);
	const searchRef = React.useRef<BCInputSearchRef>(null);

	const onFormulaItemSelect = (args: ITreeviewSelectEventArgs) => {
		if (args.item.isLeaf) {
			setSelectedKeys([args.item.key]);
			searchRef.current._clearValue();
		}
	};

	const onItemSelect = (args: ITreeviewSelectEventArgs) => {
		if (args.item.isLeaf) {
			insertIntoEditor(args.data.text);
		}
	};

	const insertIntoEditor = (text: string) => {
		const position = editor.current.getPosition();
		const range = monaco.current.Range.fromPositions(position);
		const model = editor.current.getModel();

		const prevChar = model.getValueInRange({ ...range, startColumn: range.startColumn - 1 });
		const nextChar = model.getValueInRange({ ...range, endColumn: range.endColumn + 1 });

		const prevCharNotWhiteSpace = prevChar !== ' ' && prevChar !== '';
		const nextCharNotWhiteSpace = nextChar !== ' ' && nextChar !== '';

		if (prevCharNotWhiteSpace) {
			text = ` ${text}`;
		}
		if (nextCharNotWhiteSpace) {
			text = `${text} `;
		}

		editor.current.getModel().applyEdits([{ range, text }]);
	};

	const filterFields = (items: ITreeviewItem<any>[], valueType?: IFieldType) =>
		items.filter(i => {
			if (i.children && i.children.length) {
				i.children = filterFields(i.children, valueType);
				return true;
			}

			const isProperty =
				i.dataRef && i.dataRef.type && IFieldType[i.dataRef.type as IFieldType]
					? true
					: false;
			const equalType = i.dataRef && i.dataRef.type === valueType;

			if (
				(valueType ? !isProperty || equalType : true) &&
				(isProperty ? true : i.children?.length)
			) {
				return true;
			}
		});

	const getDetailItemsBySelectedCategory = (): ITreeviewItem<any>[] => {
		if (selectedKeys && selectedKeys.length) {
			const selectedKey = selectedKeys[0];
			switch (selectedKey) {
				case 'Fields': {
					return filterFields(props.fields, IFieldType.number);
				}
				case 'Constants': {
					return getConstants();
				}
				case 'Statistics': {
					return getStatistics();
				}
				case 'Trigonometry': {
					return getTrigonometry();
				}
				case 'Operators': {
					return getOperators();
				}
				default:
					return [];
			}
		}

		return [];
	};

	const renderLeftSide = () => (
		<BCTreeview
			items={getMenuItems()}
			disableToolbar
			searchable={false}
			draggable={false}
			isTreeLeave={false}
			scrollbarStyles={mainMenuScrollbarStyles}
			hasSrollbar
			className={Styles.treeView}
			onSelect={onFormulaItemSelect}
			selectedKeys={selectedKeys}
		/>
	);

	const renderRightSide = () => (
		<BCTreeview
			getSearchInputRef={searchRef}
			items={getDetailItemsBySelectedCategory()}
			searchable
			draggable={false}
			isTreeLeave={false}
			scrollbarStyles={scrollbarStyles}
			hasSrollbar
			className={Styles.treeView}
			onSelect={onItemSelect}
			selectedKeys={selectedKeys}
		/>
	);

	const onCodeChange = (value: string) => {
		if (props.onValueChanged) {
			props.onValueChanged(value);
		}
	};

	const onCodeEditorMount = (editorInstance: ICodeEditor, monacoInstance: IMonacoEditor) => {
		editor.current = editorInstance;
		monaco.current = monacoInstance;
	};

	const getEditorValue = () => props.value;

	return (
		<div className="container-fluid">
			<div className="row mb-2">
				<div className={Styles.codeEditorContainer}>
					<BCCodeEditor
						value={getEditorValue()}
						language="js"
						width="100%"
						height="100%"
						onChange={onCodeChange}
						onMount={onCodeEditorMount}
						options={{
							minimap: {
								enabled: false
							},
							contextmenu: false,
							autoIndent: 'none'
						}}
					/>
				</div>
			</div>
			<div className="row">
				<div className="col-md-3 mt-2 mb-2">{renderLeftSide()}</div>
				<div className="col-md-9 mt-2 mb-2">{renderRightSide()}</div>
			</div>
		</div>
	);
};
export { FormulaEditorModal, BCFormulaEditor };
